import {
  Component,
  ViewEncapsulation,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { KYMW00Model } from '../../../commons/kymw00/kymw00.model';
import { KYMW4400Model } from './kymw4400.model';
import { PageInfo } from './vo/page-info.model';
import { KYMW4401Component } from '../kymw4401/kymw4401.component';
import { KYMW4402Component } from '../kymw4402/kymw4402.component';
import { KYMW4403Component } from '../kymw4403/kymw4403.component';
import { KYMW0002Component } from '../../../commons/kymw00/kymw0002/kymw0002.component';
import { PlessViewHelperSub } from '../../../commons/kymw00/pless-view-helper-sub';
import { Point } from '@grapecity/wijmo';
import { Menu } from '@grapecity/wijmo.input';
import {
  CellType,
  FlexGrid,
  CellRangeEventArgs,
  AllowMerging,
} from '@grapecity/wijmo.grid';
import { PlessAlert } from '../../../shared/pless-alert/pless-alert.component';
import { GetFeatureThemaSelectItemsService } from './api-services/get-feature-thema-select-items.service';
import { GetPageInfoListService } from './api-services/get-page-info-list.service';
import { GetPageTemplateSelectItemsService } from './api-services/get-page-template-select-items.service';
import { GetPublishGoodsGroupSelectItemsService } from './api-services/get-publish-goods-group-select-items.service';
import { GetUserManagementSelectItemsService } from './api-services/get-user-management-select-items.service';
import { OutputPageInfoCsvService } from './api-services/output-page-info-csv.service';
import { RegistPageInfoListService } from './api-services/regist-page-info-list.service';
import { RegistAutoKomaArrangeService } from './api-services/regist-auto-koma-arrange.service';
import { GetCodeListService } from '../../../commons/kymw02/api-services/get-code-list.service';
import { GetTagListService } from './api-services/get-tag-list.service';
import { GetAutoKomaArrangePatternListService } from './api-services/get-auto-koma-arrange-pattern-list.service';
import { FileDownloadConfirmService } from '../../../commons/kymw02/file-download-confirm/file-download-confirm.service';
import { PlessDialogServiceSub } from '../../../commons/kymw00/pless-dialog-service-sub';
import { PlessViewUtilsSub } from './../../../commons/kymw00/pless-view-utils-sub.service';
import { EventArgs } from '@grapecity/wijmo';
import { DataGridUtil } from '../../../commons/kymw00/data-grid-util';

/**
 * ページ・台割編集を管理するクラス
 *
 * @export
 * @class KYMW4400Component
 * @extends {PlessViewHelperSub}
 * @implements {OnInit}
 */
@Component({
  selector: 'app-kymw4400',
  templateUrl: './kymw4400.component.html',
  styleUrls: ['./kymw4400.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class KYMW4400Component
  extends PlessViewHelperSub
  implements OnInit, AfterViewInit
{
  public model: KYMW4400Model = new KYMW4400Model();
  private dragIndex: number;
  private dummyId = 0;
  private pageInfoBefore = '';

  private static readonly CODE_DIV_AUTO_ARRANGE_PATTERN = 'ctp204';
  private static readonly CODE_DIV_COMMON_PUBLISH_FLG = 'ctp254';
  private static readonly FILE_NAME_PREFIX = 'ページ・台割表';
  private static readonly FILE_NAME_DELIMITER = '_';
  private static readonly FILE_NAME_SUFFIX = '.csv';
  private static readonly DUMMY_PREFIX = 'dmy';
  private static readonly AUTO_ARRANGE_PATTERN_1 = '001'; // ハピデリ中面
  private static readonly AUTO_ARRANGE_PATTERN_2 = '002'; // 農産
  private static readonly AUTO_ARRANGE_PATTERN_3 = '003'; // 前回コピー

  private context = new Menu(document.createElement('div'), {
    displayMemberPath: 'caption',
    selectedValuePath: 'cmd',
    itemsSource: [
      { caption: '一行上に行追加', cmd: 'addRowContext', enabled: true },
      { caption: '行削除', cmd: 'delRowContext', enabled: true },
    ],
    command: {
      canExecuteCommand: param => {
        return (
          this.model.isEditable && this.isPermittedFullControl && param.enabled
        );
      },
    },
    itemClicked: () => {
      if (this.context.selectedValue === 'addRowContext') this.addRowContext();
      if (this.context.selectedValue === 'delRowContext') this.delRowContext();
    },
  });

  // バリデーション 設定ボタン用
  private validationsForPage: {
    invalid: (m: PageInfo[]) => boolean;
    code: string;
    messages: string[];
  }[] = [
    {
      invalid: m => m.some(_ => PlessViewUtilsSub.isEmpty(_.pageNum)),
      code: 'W0001',
      messages: ['ページNO'],
    },
    { // ページ一覧内での重複チェック
      invalid: () =>
        this.model.pageList.length !== new Set(this.model.pageList.map(_ => _.pageNum)).size,
      code: 'E0115',
      messages: ['ページNO'],
    },
    {
      invalid: m => m.some(_ => PlessViewUtilsSub.isEmpty(_.folio)),
      code: 'W0001',
      messages: ['ノンブル'],
    },
    { // ページ一覧内での重複チェック
      invalid: () =>
        this.model.pageList.length !== new Set(this.model.pageList.map(_ => _.folio)).size,
      code: 'E0115',
      messages: ['ノンブル'],
    },
    {
      invalid: m => m.some(_ => PlessViewUtilsSub.isEmpty(_.pageInfo)),
      code: 'W0001',
      messages: ['ページ名称'],
    },
    {
      invalid: m =>
        m.some(_ => PlessViewUtilsSub.isEmpty(_.contactUser1)),
      code: 'E0103',
      messages: ['ページ担当者１'],
    },
    {
      invalid: m =>
        m.some(
          _ =>
            _.contactUser1 === _.contactUser2 ||
            _.contactUser1 === _.contactUser3
        ),
      code: 'E0115',
      messages: ['ページ担当者'],
    },
    {
      invalid: m =>
        m.some(
          _ => _.contactUser2 && _.contactUser2 === _.contactUser3
        ),
      code: 'E0115',
      messages: ['ページ担当者'],
    },
    {
      invalid: m =>
        m.some(_ => 
          _.autoArrangePattern === KYMW4400Component.AUTO_ARRANGE_PATTERN_1 &&
          _.publishGoodsGroupIdList.length < 1 && PlessViewUtilsSub.isEmpty(_.feature1) &&
          PlessViewUtilsSub.isEmpty(_.feature2) && PlessViewUtilsSub.isEmpty(_.feature1)
        ),
      code: 'W0036',
      messages: ['自動コマ割パターンでハピデリ中面', '掲載商品グループもしくは特集'],
    },
    {
      invalid: m =>
        m.some(_ => 
          _.autoArrangePattern === KYMW4400Component.AUTO_ARRANGE_PATTERN_2 &&
          _.publishGoodsGroupIdList.length < 1
        ),
      code: 'W0036',
      messages: ['自動コマ割パターンで農産', '掲載商品グループ'],
    },
    {
      invalid: m =>
        m.some(
          _ =>
            _.feature1 &&
            (_.feature1 === _.feature2 || _.feature1 === _.feature3)
        ),
      code: 'E0115',
      messages: ['特集'],
    },
    {
      invalid: m =>
        m.some(_ => _.feature2 && _.feature2 === _.feature3),
      code: 'E0115',
      messages: ['特集'],
    },
    {
      invalid: m =>
        m.some(_ => PlessViewUtilsSub.isEmpty(_.commonPublishFlg)),
      code: 'W0001',
      messages: ['共通版'],
    },
    {
      invalid: m =>
        m.some(_ => 
          (PlessViewUtilsSub.isEmpty(_.autoArrangePattern) || _.autoArrangePattern === 'blankVal') &&
          _.isAutoArrangePatternRequired
        ),
      code: 'W0036',
      messages: ['ページパターンで自動選択', '自動コマ割パターン'],
    },
  ];

  // バリデーション 表示ボタン用
  private validations: {
    invalid: (m: KYMW4400Model) => boolean;
    code: string;
    messages: string[];
  }[] = [
    {
      invalid: m => PlessViewUtilsSub.isEmpty(m.mediumType),
      code: 'E0103',
      messages: ['媒体'],
    },
    {
      invalid: m => PlessViewUtilsSub.isEmpty(m.orderCode),
      code: 'W0001',
      messages: ['受注コード'],
    },
    {
      invalid: m => PlessViewUtilsSub.isEmpty(m.catalogId),
      code: 'W0001',
      messages: ['企画回'],
    },
    {
      invalid: m => PlessViewUtilsSub.isEmpty(m.coopCode),
      code: 'E0103',
      messages: ['会員生協'],
    },
  ];

  // バリデーション コマ割実行ボタン用
  private validationsForKoma: {
    invalid: (m: PageInfo[]) => boolean;
    code: string;
    messages: string[];
  }[] = [
    {
      invalid: m =>
        m.some(_ => {
          return PlessViewUtilsSub.isEmpty(_.autoArrangePattern) || _.autoArrangePattern === 'blankVal';
        }),
      code: 'E0513',
      messages: ['選択した行に自動コマ割パターンが未入力の行が存在する', '自動コマ割'],
    },
  ];

  @ViewChild('dtg_PageList', { static: false }) dtg_PageList: FlexGrid;
  @ViewChild('chk_Header', { static: false }) chk_Header: ElementRef;

  /**
   * Creates an instance of KYMW4400Component.
   * @param {PlessDialogServiceSub} dialogService
   * @param {KYMW00Model} kymw00Model
   * @param {GetFeatureThemaSelectItemsService} getFeatureThemaSelectItemsService
   * @param {GetPageInfoListService} getPageInfoListService
   * @param {GetPageTemplateSelectItemsService} getPageTemplateSelectItemsService
   * @param {GetPublishGoodsGroupSelectItemsService} getPublishGoodsGroupSelectItemsService
   * @param {GetUserManagementSelectItemsService} getUserManagementSelectItemsService
   * @param {OutputPageInfoCsvService} outputPageInfoCsvService
   * @param {RegistPageInfoListService} registPageInfoListService
   * @param {RegistAutoKomaArrangeService} registAutoKomaArrangeService
   * @param {GetCodeListService} getCodeListService
   * @param {GetTagListService} getTagListService
   * @param {GetAutoKomaArrangePatternListService} getAutoKomaArrangePatternListService
   * @param {FileDownloadConfirmService} fileDownloadConfirmService
   * @memberof KYMW4400Component
   */
  constructor(
    private dialogService: PlessDialogServiceSub,
    private kymw00Model: KYMW00Model,
    private elementRef: ElementRef,
    private getFeatureThemaSelectItemsService: GetFeatureThemaSelectItemsService,
    private getPageInfoListService: GetPageInfoListService,
    private getPageTemplateSelectItemsService: GetPageTemplateSelectItemsService,
    private getPublishGoodsGroupSelectItemsService: GetPublishGoodsGroupSelectItemsService,
    private getUserManagementSelectItemsService: GetUserManagementSelectItemsService,
    private outputPageInfoCsvService: OutputPageInfoCsvService,
    private registPageInfoListService: RegistPageInfoListService,
    private registAutoKomaArrangeService: RegistAutoKomaArrangeService,
    private getCodeListService: GetCodeListService,
    private getTagListService: GetTagListService,
    private getAutoKomaArrangePatternListService: GetAutoKomaArrangePatternListService,
    private fileDownloadConfirmService: FileDownloadConfirmService
  ) {
    super(kymw00Model);
  }

  /**
   * コンポーネント初期化
   *
   * @memberof KYMW4400Component
   */
  public ngOnInit(): void {
    this.componentName = 'KYMW4400';
  }

  /**
   * ビュー初期化
   *
   * @memberof KYMW4400Component
   */
  public ngAfterViewInit(): void {
    this.dtg_PageList.rowHeaders.columns[0].minWidth = 5;
    // 掲載商品グループとタグのボタン列にスタイル適用
    this.dtg_PageList.columns[10].cssClassAll = 'btn_cellHeader';
    this.dtg_PageList.columns[15].cssClassAll = 'btn_cellHeader';
    this.dtg_PageList.itemsSourceChanged.addHandler(
      (_g: FlexGrid, _e: EventArgs) => {
        _g.columnHeaders.rows[0].height = 50;
        _g.select(-1, -1);
      }
    );
    this.dtg_PageList.hostElement.addEventListener('click', e => {
      const ht = this.dtg_PageList.hitTest(e.pageX, e.pageY);
      if (
        ht.cellType === CellType.Cell &&
        ht.col === 0 &&
        this.model.isEditable &&
        this.isPermittedFullControl
      ) {
        const val = this.dtg_PageList.getCellData(ht.row, ht.col, false);
        this.dtg_PageList.setCellData(ht.row, ht.col, val ? false : true);
      }
      if (ht.cellType === CellType.ColumnHeader && ht.col === 0) {
        this.chk_Header.nativeElement.click();
      }
    });
    this.dtg_PageList.hostElement.addEventListener('contextmenu', e => {
      e.preventDefault();
      DataGridUtil.showContextMenu(this.dtg_PageList, this.context, e);
    });
  }

  /**
   * グリッド初期処理
   *
   * @memberof KYMW4400Component
   */
  public gridInit(grid: FlexGrid) {
    grid.columnHeaders.rows[0].height = 50;
    grid.select(-1, -1);
  }

  /**
   * ボタン押下 表示
   *
   * @memberof KYMW4400Component
   */
  public getList() {
    // 変更チェック ダイアログ表示
    if (
      this.model.isEditable &&
      (this.model.registPages.length > 0 ||
        this.model.deletePages.length > 0 ||
        this.model.updatePages.length > 0)
    ) {
      this.dialogService.openAlertGetMessage('Q0171', [], e => {
        if (e === PlessAlert.Yes) {
          this.registPage(() => this.getPageList(false));
        } else {
          this.getPageList(false);
        }
      });
    } else {
      this.getPageList(false);
    }
  }

  /**
   * ボタン押下 コピー
   *
   * @memberof KYMW4400Component
   */
  public copy() {
    // コピー設定画面を呼び出す処理
    const openModal = () => {
      const componentRef = this.dialogService.openModal(KYMW4401Component);
      const instance = componentRef.componentInstance as KYMW4401Component;
      instance.parent = this;
      if (this.model.searchedMediumType) {
        instance.setParams({
          mediumType: this.model.searchedMediumType,
          coopCode: this.model.searchedCoopCode,
        });
      } else {
        instance.setParams({
          mediumType: this.model.mediumType,
          coopCode: this.model.coopCode,
        });
      }
      instance.closeFunction = () => {
        componentRef.close();
        const params = instance.getParams();
        if (!params) return;
        this.model.mediumType = params.mediumType;
        this.model.orderCode = params.orderCode;
        this.model.catalogId = params.catalogId;
        this.model.coopCode = params.coopCode;
        this.getPageList(false);
      };
    };

    // 変更チェック ダイアログ表示
    if (
      this.model.isEditable &&
      (this.model.registPages.length > 0 ||
        this.model.deletePages.length > 0 ||
        this.model.updatePages.length > 0)
    ) {
      this.dialogService.openAlertGetMessage('Q0171', [], e => {
        if (e === PlessAlert.Yes) {
          this.registPage(() => {
            this.getPageList(true);
            openModal();
          });
        } else {
          openModal();
        }
      });
    } else {
      openModal();
    }
  }

  /**
   * ボタン押下 行追加
   *
   * @memberof KYMW4400Component
   */
  public addRow() {
    this.model.pageList.push(new PageInfo(this.getDummyId()));
    this.dtg_PageList.collectionView.refresh();
    this.dtg_PageList.refresh(true);
    this.dtg_PageList.scrollPosition = new Point(
      0,
      -this.dtg_PageList.columnHeaders.height - this.dtg_PageList.cells.height
    );
    this.dtg_PageList.collectionView.refresh();
  }

  /**
   * ボタン押下 行削除
   *
   * @memberof KYMW4400Component
   */
  public delRow() {
    this.model.pageList = this.model.pageList.filter(_ => !_.check);
    this.dtg_PageList.collectionView.refresh();
  }

  /**
   * ボタン押下 設定
   *
   * @param calbak API正常応答時コールバック
   * @memberof KYMW4400Component
   */
  public registPage(calbak: () => void = () => this.getPageList(true)) {
    // ページ一覧の追加フラグ、更新フラグがTRUEである行を検索し、該当行に対してバリデーションを行う。
    const registPages = this.model.registPages;
    const updatePages = this.model.updatePages;

    // バリデーション
    //追加行
    if (registPages.length > 0) {
      const registInvaldItem = this.validationsForPage.find(_ => _.invalid(registPages));
      if (registInvaldItem) {
        this.dialogService.openAlertGetMessage(
          registInvaldItem.code,
          registInvaldItem.messages
        );
        return;
      }
    }
    
    //更新行
    if (updatePages.length > 0) {
      const updateInvalidItem = this.validationsForPage.find(_ => _.invalid(updatePages));
      if (updateInvalidItem) {
        this.dialogService.openAlertGetMessage(
          updateInvalidItem.code,
          updateInvalidItem.messages
        );
        return;
      }
    }

    this.registPageInfoListService.execute(this.model, calbak);
  }

  /**
   * コマ割実行用 設定
   *
   * @memberof KYMW4400Component
   */
  private async asyncRegistPageForKoma() {
    this.registPage(() => {});
  }

  /**
   * コマ割実行用 ページ一覧検索
   *
   * @memberof KYMW4400Component
   */
  private async asyncGetPageListForKoma() {
    if (
      this.model.registPages.length > 0 ||
      this.model.deletePages.length > 0 ||
      this.model.updatePages.length > 0
    ) {
      this.model.mediumType = this.model.searchedMediumType;
      this.model.orderCode = this.model.searchedOrderCode;
      this.model.catalogId = this.model.searchedCatalogId;
      this.model.coopCode = this.model.searchedCoopCode;

      await this.asyncApi();
    }
  }

  /**
   * コマ割実行用 
   *
   * @param target 選択されたリスト
   * @memberof KYMW4400Component
   */
  private async asyncRegistKoma(target: PageInfo[]) {
    // 再取得後のリスト
    const updatedPageList = this.model.pageList;
    const beforePageList = target;
    target = updatedPageList.filter(page =>
      beforePageList.some(original => original.pageNum === page.pageNum)
    );

    // バリデーション
    const invaldItem = this.validationsForKoma.find(_ => _.invalid(target));
    if (invaldItem) {
      this.dialogService.openAlertGetMessage(
        invaldItem.code,
        invaldItem.messages
      );
      return;
    }

    this.registAutoKomaArrangeService.execute(this.model, target, (res) => {
      this.getPageList(true);
      if (res && 'MsgId' in res && !PlessViewUtilsSub.isEmpty(res.MsgId)) {
        this.dialogService.openAlertGetMessage(res.MsgId, [res.PageNumText]);
      }
    });
  }

  /**
   * ボタン押下 コマ割実行
   *
   * @memberof KYMW4400Component
   */
  public async registKoma() {
    // 対象データを保持
    const target = this.model.registKoma;

    // ページ一覧の設定処理
    await this.asyncRegistPageForKoma();
    await this.asyncGetPageListForKoma();

    await this.asyncRegistKoma(target);
  }

  /**
   * ボタン押下 他生協へコピー
   *
   * @memberof KYMW4400Component
   */
  public copyCoops() {
    // 生協間コピー設定画面を呼び出す処理
    const openModal = () => {
      const componentRef = this.dialogService.openModal(KYMW4402Component);
      const instance = componentRef.componentInstance as KYMW4402Component;
      instance.parent = this;
      instance.setParams({
        mediumType: this.model.searchedMediumType,
        orderCode: this.model.searchedOrderCode,
        catalogId: this.model.searchedCatalogId,
        coopCode: this.model.searchedCoopCode,
      });
      instance.closeFunction = () => {
        componentRef.close();
      };
    };

    // 変更チェック ダイアログ表示
    if (
      this.model.registPages.length > 0 ||
      this.model.deletePages.length > 0 ||
      this.model.updatePages.length > 0
    ) {
      this.dialogService.openAlertGetMessage('Q0171', [], e => {
        if (e === PlessAlert.Yes) {
          this.registPage(() => {
            this.getPageList(true);
            openModal();
          });
        } else {
          openModal();
        }
      });
    } else {
      openModal();
    }
  }

  /**
   * ボタン押下 CSV出力
   *
   * @memberof KYMW4400Component
   */
  public outputCsv() {
    const output = () => {
      this.model.csvPath = '';
      this.outputPageInfoCsvService.execute(this.model, () => {
        const fname =
          [
            KYMW4400Component.FILE_NAME_PREFIX,
            this.model.searchedMediumType,
            this.model.searchedOrderCode,
            this.model.searchedCoopCode,
            PlessViewUtilsSub.formatedNowDate('YYYYMMDD_hhmmss'),
          ].join(KYMW4400Component.FILE_NAME_DELIMITER) +
          KYMW4400Component.FILE_NAME_SUFFIX;
        this.fileDownloadConfirmService.show(this.model.csvPath, fname);
      });
    };

    // 変更チェック 確認ダイアログ
    if (
      this.model.registPages.length > 0 ||
      this.model.deletePages.length > 0 ||
      this.model.updatePages.length > 0
    ) {
      this.dialogService.openAlertGetMessage('Q0171', [], e => {
        if (e === PlessAlert.Yes) {
          this.registPage(() => {
            this.getPageList(true);
            output();
          });
        } else {
          output();
        }
      });
    } else {
      output();
    }
  }

  /**
   * コンテキストメニュー 行追加
   *
   * @memberof KYMW4400Component
   */
  public addRowContext() {
    const index = this.dtg_PageList.selection.row;
    this.model.pageList.splice(index, 0, new PageInfo(this.getDummyId()));
    this.dtg_PageList.collectionView.refresh();
  }

  /**
   *  コンテキストメニュー 行削除
   *
   * @memberof KYMW4400Component
   */
  public delRowContext() {
    if (!(this.dtg_PageList.selectedItems[0] instanceof PageInfo)) return;
    const selItem = this.dtg_PageList.selectedItems[0];
    this.model.pageList = this.model.pageList.filter(
      _ => selItem.pageId !== _.pageId
    );
    this.dtg_PageList.collectionView.refresh();
  }

  /**
   * 掲載商品グループ 選択
   *
   * @memberof KYMW4400Component
   */
  public setPublishGoodsGroup(pid: string) {
    const sel = this.model.pageList.find(_ => pid === _.pageId);
    if (!sel) return;
    // 掲載商品グループ選択画面を呼び出す処理
    const componentRef = this.dialogService.openModal(KYMW4403Component);
    const instance = componentRef.componentInstance as KYMW4403Component;
    instance.parent = this;
    instance.setParams({
      selectedList: sel.publishGoodsGroupIdList,
      orderCode: this.model.searchedOrderCode,
      mediumType: this.model.searchedMediumType,
    });
    instance.closeFunction = () => {
      componentRef.close();
      const params = instance.getParams();
      if (!params) return;
      sel.publishGoodsGroupIdList = params;
      sel.setPublishGoodsGroupName(this.model.publishGoodsGroupSelectItems);
      this.dtg_PageList.collectionView.refresh();
    };
  }

  /**
   * タグ 選択
   *
   * @memberof KYMW4400Component
   */
  public setTag(pid: string) {
    const sel = this.model.pageList.find(_ => pid === _.pageId);
    if (!sel) return;
    // タグ選択画面を呼び出す処理
    const componentRef = this.dialogService.openModal(KYMW0002Component);
    const instance = componentRef.componentInstance as KYMW0002Component;
    instance.parent = this;
    instance.setParams(sel.tagIdList);
    instance.closeFunction = () => {
      componentRef.close();
      const params = instance.getParams();
      if (!params) return;
      sel.tagIdList = params.map(_ => _.tagId);
      sel.setTagName(this.model.tagSelectItems);
      this.dtg_PageList.collectionView.refresh();
    };
  }

  /**
   * ページ一覧検索 実処理
   *
   * @param sameConditions 直近条件での検索を指定
   * @memberof KYMW4400Component
   */
  private getPageList(sameConditions: boolean): void {
    if (sameConditions) {
      this.model.mediumType = this.model.searchedMediumType;
      this.model.orderCode = this.model.searchedOrderCode;
      this.model.catalogId = this.model.searchedCatalogId;
      this.model.coopCode = this.model.searchedCoopCode;
    } else {
      // バリデーション
      const invalidItem = this.validations.find(_ => _.invalid(this.model));
      if (invalidItem) {
        this.dialogService.openAlertGetMessage(
          invalidItem.code,
          invalidItem.messages
        );
        return;
      }
    }

    this.asyncApi();
  }

  /**
   * ページ一覧検索 APIアクセス
   *
   * @memberof KYMW4400Component
   */
  private asyncApi = async () => {
    try {
      await this.getFeatureThemaSelectItemsService.executeAsync(this.model);
      await this.getPublishGoodsGroupSelectItemsService.executeAsync(
        this.model
      );
      await this.getTagListService.executeAsync(this.model);
      await this.getUserManagementSelectItemsService.executeAsync(this.model);
      await this.getPageTemplateSelectItemsService.executeAsync(this.model);
      await this.getAutoKomaArrangePatternListService.executeAsync(
        this.model,
        KYMW4400Component.CODE_DIV_AUTO_ARRANGE_PATTERN,
      );
      await this.getCodeListService.executeAsync(
        KYMW4400Component.CODE_DIV_COMMON_PUBLISH_FLG,
        '',
        this.model,
        'commonPublishFlgSelectItems'
      );
      await this.getPageInfoListService.executeAsync(this.model);
      if (this.chk_Header.nativeElement.checked) this.chk_Header.nativeElement.checked = false;
    } catch (e) {}
    this.dtg_PageList.collectionView.refresh();

    // ページパターンが自動選択の場合、自動コマ割パターンは必須
    this.model.pageList.forEach(info => {
      info.isAutoArrangePatternRequired = info.pageTemplateId === KYMW4400Model.PAGE_TEMPLATE_AUTOSELECT;
    })
  };

  /**
   * 新規登録用ID
   *
   * @memberof KYMW4400Component
   */
  private getDummyId(): string {
    return (
      KYMW4400Component.DUMMY_PREFIX + String(this.dummyId++).padStart(5, '0')
    );
  }

  /**
   * ヘッダーチェックボックス 入力
   *
   * @memberof KYMW4400Component
   */
  public headerCheck() {
    const val = this.chk_Header.nativeElement.checked ?? false;
    this.model.pageList.forEach(_ => (_.check = val));
  }

  /**
   * ページNO blur
   *
   * @memberof KYMW4400Component
   */
  public pageNumBlur(e: any, id?: string) {
    if (this.dialogService.isOpened()) return;
    const valStr = e?.target?.value;
    if (!valStr) return;
    if (/^0./.test(valStr)) return;
    let val = parseInt(valStr);
    if (!val && val !== 0) return;
    if (!id) return;

    this.dialogService.openAlertGetMessage('Q0172', ['ページNO'], e => {
      if (e === PlessAlert.Yes) {
        const rIndex = this.model.pageList.map(_ => _.pageId).indexOf(id);
        this.model.pageList
          .filter((_, i) => i > rIndex)
          .forEach(_ => (_.pageNum = (++val).toString()));
      } else {
        return;
      }
    });
  }

  /**
   * ノンブル blur
   *
   * @memberof KYMW4400Component
   */
  public folioBlur(e: any, id?: string) {
    if (this.dialogService.isOpened()) return;
    const valStr = e?.target?.value;
    if (!valStr) return;
    if (/^0./.test(valStr)) return;
    let val = parseInt(valStr);
    if (!val && val !== 0) return;
    if (!id) return;

    this.dialogService.openAlertGetMessage('Q0172', ['ノンブル'], e => {
      if (e === PlessAlert.Yes) {
        const rIndex = this.model.pageList.map(_ => _.pageId).indexOf(id);
        this.model.pageList
          .filter((_, i) => i > rIndex)
          .forEach(_ => (_.folio = (++val).toString()));
      } else {
        return;
      }
    });
  }

  /**
   * ページ名称 focus
   *
   * @memberof KYMW4400Component
   */
  public pageInfoFocus(str?: string) {
    this.pageInfoBefore = str;
  }

  /**
   * ページテンプレート change
   * 
   * @param e 
   * @memberof KYMW4400Component
   */
  public pageTemplateChange(e: any) {
    const selectElement = e.target as HTMLSelectElement;
    const selectRow = this.dtg_PageList.selectedItems[0];
    // ページテンプレートが「自動選択」の場合、自動コマ割パターンを必須にする
    selectRow.isAutoArrangePatternRequired = selectElement.value === KYMW4400Model.PAGE_TEMPLATE_AUTOSELECT;
  }

  /**
   * 行入れ替え処理
   *
   * @memberof KYMW4400Component
   */
  onDraggingRow(s: FlexGrid, e: CellRangeEventArgs) {
    this.dragIndex = e.row;
    s.collectionView.moveCurrentToPosition(this.dragIndex);
  }

  /**
   * 行入れ替え処理
   *
   * @memberof KYMW4400Component
   */
  onDraggedRow(s: FlexGrid, e: CellRangeEventArgs) {
    let dropIndex = e.row,
      arr = s.collectionView.sourceCollection;
    s.collectionView.deferUpdate(() => {
      let item = arr[this.dragIndex];
      arr.splice(this.dragIndex, 1);
      arr.splice(dropIndex, 0, item);
      s.collectionView.moveCurrentToPosition(dropIndex);
    });
  }

  get enableCopy() {
    return !PlessViewUtilsSub.isEmpty(this.model.mediumType) &&
      !PlessViewUtilsSub.isEmpty(this.model.orderCode) &&
      !PlessViewUtilsSub.isEmpty(this.model.catalogId) &&
      !PlessViewUtilsSub.isEmpty(this.model.coopCode);
  }

  /**
   * blankValを空文字に変換
   * HTMLで使用
   * 
   * @param value 
   * @returns 
   */
  public blankValChangeEmpty(value: string): string {
    return value === 'blankVal' ? '': value;
  }
}
